import angular from 'angular'
import each from 'lodash/each'
import isNumber from 'lodash/isNumber'
import merge from 'lodash/merge'

import { $log, $q } from '../../ng-compat/imports'
import { store } from './database'

/**
 * Responsible for persisting version data from the /api/2.0/versions end point.
 *
 * @module versionsStore
 */
export function VersionsStoreFactory() {
  const versionsDbStore = store('versions')

  /**
   * Persist an EntityVersion to the `versions` IndexedDB store.
   *
   * @private
   * @method storeEntityVersion
   *
   * @param entityVersion {EntityVersion}
   *      The EntityVersion to persist to the IndexedDB store
   *
   * @return {Promise<EntityVersion>} A promise resolved with the EntityVersion once it has been persisted.
   */
  function storeEntityVersion(entityVersion: $TSFixMe) {
    return versionsDbStore.put(entityVersion).then(function () {
      return entityVersion
    })
  }

  /**
   * Stores version information for syncing versioned entities (e.g. products, outlets).
   *
   * @class VersionsStore
   */
  return {
    /**
     * Ensures there is an entry in the VersionsStore for tracking the given entity.
     *
     * @method trackVersion
     *
     * @param entityName {String}
     *      The name of the entity to track the version of (as specified in the /api/2.0/versions end point)
     *
     * @param [entityStoreName] {String}
     *      Name of the entity store the data is stored in (e.g. if storing/syncing different data from the same
     *      end point such as active products and inactive products). Defaults to the same value as `entityName`
     *
     * return {Promise<EntityVersion>} A promise resolved with the version tracking information that is in the
     *      IndexedDB store for this entity
     */
    trackVersion(entityName: string, entityStoreName?: string) {
      entityStoreName = entityStoreName || entityName

      $log.debug(
        '[VersionsStore] Tracking version of entity: "' +
          entityName +
          '", ' +
          'entityStore: "' +
          entityStoreName +
          '"'
      )

      return versionsDbStore
        .get(entityStoreName)
        .then(function (entityVersion: $TSFixMe) {
          if (!entityVersion) {
            entityVersion = {
              entityStoreName,
              entityName,
              lastSyncedVersion: 0,
              maxVersion: 0,
              initialSyncHasOccurred: false,
            }

            return storeEntityVersion(entityVersion)
          } else {
            return entityVersion
          }
        })
    },

    /**
     * Update the max versions in the version store with the latest version data from the server and determine
     * which tracked entities need to be synced.
     *
     * @method updateVersions
     *
     * @param latestVersionData {Object}
     *      The latest version data in the form of { <entityName>: <version> }
     *      e.g. { outlets: 175, products: 391 }
     *
     * @return {Promise<Array<EntityVersion>>} A promise resolved with an Array of entities that need to be
     *      synced (i.e. have max version greater than last synced version)
     */
    updateVersions(latestVersionData: any) {
      $log.debug(
        '[VersionsStore] Latest version data from server:',
        latestVersionData
      )
      return versionsDbStore.all().then(function (entityVersions: $TSFixMe) {
        const entityVersionsBeingUpdated: $TSFixMe[] = []
        const entityVersionsToSync: $TSFixMe[] = []
        each(entityVersions, function (entityVersion) {
          const latestMaxVersion = latestVersionData[entityVersion.entityName]

          if (!isNumber(latestMaxVersion)) {
            // Server didn't have a valid version for this entity, skip
            return
          }

          // Only update the IndexedDB store if we get a newer version from the server
          if (latestMaxVersion > entityVersion.maxVersion) {
            entityVersion.maxVersion = latestMaxVersion

            $log.debug(
              '[VersionsStore] Updating version data for:',
              entityVersion
            )
            entityVersionsBeingUpdated.push(versionsDbStore.put(entityVersion))
          }

          // Identify tracked versions that need to be synced with the server
          if (entityVersion.maxVersion > entityVersion.lastSyncedVersion) {
            entityVersionsToSync.push(entityVersion)
          }
        })

        return $q.all(entityVersionsBeingUpdated).then(function () {
          $log.debug(
            '[VersionsStore] ' +
              entityVersionsToSync.length +
              ' tracked entities require sync:',
            entityVersionsToSync
          )

          return entityVersionsToSync
        })
      })
    },

    /**
     * Updates the specified EntityVersion in the `versions` IndexedDB store with the given version data.
     *
     * @method updateEntityVersion
     *
     * @param entityVersion {EntityVersion}
     *      The EntityVersion to update. Must contain at least the `entityStoreName` and then any properties that should
     *      be updated.
     *
     * @return {Promise} A promise resolved with the updated version information for the given EntityVersion
     */
    updateEntityVersion(entityVersion: $TSFixMe) {
      return versionsDbStore
        .get(entityVersion.entityStoreName)
        .then(function (trackedEntityVersion: $TSFixMe) {
          return storeEntityVersion(merge(trackedEntityVersion, entityVersion))
        })
    },

    /**
     * Get the EntityVersion for a specified entity store.
     *
     * @method getEntityVersion
     *
     * @param entityStoreName {String}
     *      Name of the entity store the data is stored in and the key the EntityVersion is stored against
     *
     * @return {Promise} A promise resolved with the version information for the given entity store name
     */
    getEntityVersion(entityStoreName: string) {
      return versionsDbStore.get(entityStoreName)
    },
    /**
     * Remove the EntityVersion for a specified entity store.
     *
     * @method removeEntityVersion
     *
     * @param entityStoreName {String}
     *      Name of the entity store the data is stored in and the key the EntityVersion is stored against
     *
     * @return {Promise} A void promise resolved
     */
    removeEntityVersion(entityStoreName: string) {
      return versionsDbStore.del(entityStoreName)
    },
  }
}

export const versionsStore = VersionsStoreFactory()

angular
  .module('webregister.factories')
  .factory('versionsStore', () => versionsStore)

// ======================================== EntityVersion class ======================================== //

// This class definition and subsequent properties define the object structure stored in the `versions` IndexedDB
// store for additional clarity and the option of referencing it as a type in method docs (e.g. {EntityVersion}).

/**
 * Version tracking information of a synchronised entity for a particular entity store (e.g. active products).
 *
 * @private
 * @static
 * @class EntityVersion
 */

/**
 * Name of the IndexedDB store that will house these entities. Often the same as `entity` but can be different if
 * syncing the same entity in two different ways (e.g. active vs inactive products).
 *
 * @property entityStoreName
 * @type String
 */

/**
 * Name of the entity being sychronised - must match the end point e.g. "outlet_taxes" for /api/2.0/outlet_taxes.
 *
 * @property entityName
 * @type String
 */

/**
 * Latest version of the entity synced with the server.
 *
 * @property lastSyncedVersion
 * @type Number
 */

/**
 * Last known max version of the entity available on the server.
 *
 * @property maxVersion
 * @type Number
 */

/**
 * Whether the initial sync has completed for this entity.
 *
 * @property initialSyncHasOccurred
 * @type {Boolean}
 */
